import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Music2, Search, Radio, ExternalLink } from "lucide-react";
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export const PLATFORMS = {
  spotify:      { label: "Spotify",       color: "#1DB954" },
  jioSaavn:     { label: "JioSaavn",      color: "#2BC5B4" },
  youtubeMusic: { label: "YouTube Music", color: "#FF0033" },
  youtube:      { label: "YouTube",       color: "#FF0000" },
  appleMusic:   { label: "Apple Music",   color: "#FA243C" },
  amazonMusic:  { label: "Amazon Music",  color: "#00A8E1" },
  instagram:    { label: "Instagram",     color: "#E1306C" },
  gaana:        { label: "Gaana",         color: "#E72C30" },
  shazam:       { label: "Shazam",        color: "#0088FF" },
  soundcloud:   { label: "SoundCloud",    color: "#FF7700" },
};

export default function UserDsp() {
  return <DspSurface scope="user" />;
}

/** Reusable panel used by both user route and admin user detail. */
export function DspSurface({ scope, userId }) {
  const base = scope === "admin" ? `/admin/user-dsp/${userId}` : "/user-dsp";
  const [records, setRecords] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [q, setQ] = useState("");
  const [lang, setLang] = useState("all");
  const [year, setYear] = useState("all");
  const [platform, setPlatform] = useState("all");
  const [label, setLabel] = useState("all");
  const [missing, setMissing] = useState("all");
  const [selected, setSelected] = useState(null);
  const [trendWindow, setTrendWindow] = useState("12m");

  useEffect(() => {
    api.get(`${base}/records`).then((r) => setRecords(r.data || []));
    api.get(`${base}/analytics`).then((r) => setAnalytics(r.data));
  }, [base]);

  const labels = useMemo(
    () => Array.from(new Set(records.map((r) => r.label).filter(Boolean))).sort(),
    [records]
  );
  const languages = useMemo(
    () => Array.from(new Set(records.map((r) => r.language).filter(Boolean))).sort(),
    [records]
  );
  const years = useMemo(() => {
    const s = new Set();
    records.forEach((r) => { if (r.release_date) s.add(r.release_date.slice(0, 4)); });
    return Array.from(s).sort().reverse();
  }, [records]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return records.filter((r) => {
      if (ql) {
        const hay = [r.song_name, r.artist_singer, r.upc, r.isrc, r.album, r.label]
          .filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(ql)) return false;
      }
      if (lang !== "all" && r.language !== lang) return false;
      if (year !== "all" && (!r.release_date || !r.release_date.startsWith(year))) return false;
      if (label !== "all" && r.label !== label) return false;
      if (platform !== "all" && !(r.links && r.links[platform])) return false;
      if (missing !== "all" && r.links && r.links[missing]) return false;
      return true;
    });
  }, [records, q, lang, year, platform, label, missing]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">
        {scope === "admin" ? "Admin · User" : "Your Catalog"}
      </div>
      <h1 className="mt-2 text-4xl font-bold tracking-tight flex items-center gap-3">
        <Radio className="w-8 h-8 text-cyan-400" /> DSP Links
      </h1>
      <p className="mt-2 text-sm text-zinc-500 max-w-2xl">
        Every song's live status across Spotify, JioSaavn, YouTube Music, Apple, Amazon and more —
        pulled from your imported DSP sheet.
      </p>

      {/* Summary cards */}
      {analytics && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Stat k="Total DSP Songs" v={analytics.total} testid="dsp-total" />
          <Stat k="Live on Spotify" v={analytics.per_platform?.spotify || 0} tone="emerald" testid="dsp-spotify" />
          <Stat k="Live on JioSaavn" v={analytics.per_platform?.jioSaavn || 0} tone="emerald" testid="dsp-jiosaavn" />
          <Stat k="Live on YT Music" v={analytics.per_platform?.youtubeMusic || 0} tone="emerald" testid="dsp-ytmusic" />
          <Stat k="Live on Apple" v={analytics.per_platform?.appleMusic || 0} tone="emerald" testid="dsp-apple" />
          <Stat k="Missing any link" v={analytics.songs_missing_links} tone="rose" testid="dsp-missing" />
        </div>
      )}

      {/* Charts */}
      {analytics && (
        <div className="mt-6 grid lg:grid-cols-3 gap-4">
          <Card title="Platform Coverage">
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={Object.keys(PLATFORMS).map((k) => ({ platform: PLATFORMS[k].label, songs: analytics.per_platform?.[k] || 0, fill: PLATFORMS[k].color }))}>
                <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                <XAxis dataKey="platform" tick={{ fill: "#71717a", fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={70} />
                <YAxis tick={{ fill: "#71717a", fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a" }} />
                <Bar dataKey="songs">
                  {Object.keys(PLATFORMS).map((k) => (<Cell key={k} fill={PLATFORMS[k].color} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
          <Card title="DSP Coverage" subtitle={`${analytics.coverage_percent}% songs have at least one link`}>
            <ResponsiveContainer width="100%" height={230}>
              <PieChart>
                <Pie data={[
                  { name: "With links", value: analytics.songs_with_links, fill: "#22d3ee" },
                  { name: "No links",   value: analytics.songs_without_links, fill: "#3f3f46" },
                ]} innerRadius={55} outerRadius={80} dataKey="value" label={{ fill: "#a1a1aa", fontSize: 11 }}>
                </Pie>
                <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a" }} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
          <Card title="Songs by Language">
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={Object.entries(analytics.per_language || {}).map(([k, v]) => ({ lang: k || "—", songs: v }))}>
                <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                <XAxis dataKey="lang" tick={{ fill: "#71717a", fontSize: 11 }} />
                <YAxis tick={{ fill: "#71717a", fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a" }} />
                <Bar dataKey="songs" fill="#a78bfa" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>
      )}

      {/* Release trend */}
      {analytics && (
        <div className="mt-6">
          <Card
            title="Release Trend"
            action={
              <Select value={trendWindow} onValueChange={setTrendWindow}>
                <SelectTrigger data-testid="dsp-trend-window" className="bg-zinc-900 border-zinc-800 h-8 text-xs w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6m">Last 6 months</SelectItem>
                  <SelectItem value="12m">Last 12 months</SelectItem>
                  <SelectItem value="all">All time</SelectItem>
                </SelectContent>
              </Select>
            }
          >
            <ResponsiveContainer width="100%" height={230}>
              <LineChart data={pickTrend(analytics.release_trend || [], trendWindow)}>
                <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fill: "#71717a", fontSize: 11 }} />
                <YAxis tick={{ fill: "#71717a", fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a" }} />
                <Line type="monotone" dataKey="count" stroke="#22d3ee" strokeWidth={2} dot={{ fill: "#22d3ee" }} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="mt-8 rounded-2xl border border-zinc-800 bg-zinc-950/60 p-4 flex flex-wrap items-end gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="dsp-search"
            placeholder="Search by Song Name, UPC, ISRC or Artist"
            className="pl-9 bg-zinc-900 border-zinc-800" />
        </div>
        <FilterSelect label="Language" value={lang} onChange={setLang} testid="dsp-filter-language"
          options={[{ v: "all", l: "All" }, ...languages.map((x) => ({ v: x, l: x }))]} />
        <FilterSelect label="Release Year" value={year} onChange={setYear} testid="dsp-filter-year"
          options={[{ v: "all", l: "All" }, ...years.map((x) => ({ v: x, l: x }))]} />
        <FilterSelect label="Label" value={label} onChange={setLabel} testid="dsp-filter-label"
          options={[{ v: "all", l: "All" }, ...labels.map((x) => ({ v: x, l: x }))]} />
        <FilterSelect label="On Platform" value={platform} onChange={setPlatform} testid="dsp-filter-platform"
          options={[{ v: "all", l: "All" }, ...Object.entries(PLATFORMS).map(([k, m]) => ({ v: k, l: m.label }))]} />
        <FilterSelect label="Missing" value={missing} onChange={setMissing} testid="dsp-filter-missing"
          options={[{ v: "all", l: "None" }, ...Object.entries(PLATFORMS).map(([k, m]) => ({ v: k, l: `Missing ${m.label}` }))]} />
        <div className="text-xs text-zinc-500 ml-auto">{filtered.length} / {records.length} songs</div>
      </div>

      {/* Song list */}
      <div className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="p-3">Song</th>
              <th className="p-3">Album</th>
              <th className="p-3">Artist</th>
              <th className="p-3">Language</th>
              <th className="p-3">Release</th>
              <th className="p-3">ISRC / UPC</th>
              <th className="p-3">Available On</th>
            </tr>
          </thead>
          <tbody data-testid="dsp-tbody">
            {filtered.length === 0 && (
              <tr><td colSpan="7" className="p-16 text-center text-zinc-500">
                <Music2 className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
                {records.length === 0
                  ? "No DSP records yet — ask admin to import your Excel."
                  : "No songs match the filters."}
              </td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} data-testid={`dsp-row-${r.id}`} className="border-b border-zinc-900 hover:bg-zinc-900/40 cursor-pointer" onClick={() => setSelected(r)}>
                <td className="p-3 font-medium">{r.song_name || "—"}</td>
                <td className="p-3 text-zinc-400">{r.album || "—"}</td>
                <td className="p-3 text-zinc-400">{r.artist_singer || "—"}</td>
                <td className="p-3 text-zinc-400">{r.language || "—"}</td>
                <td className="p-3 text-zinc-400 font-mono text-xs">{r.release_date || "—"}</td>
                <td className="p-3 font-mono text-[11px] text-zinc-500">
                  <div>{r.isrc || "—"}</div>
                  <div>{r.upc || "—"}</div>
                </td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(r.links || {}).slice(0, 8).map(([k, url]) => url && (
                      <a key={k} href={url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                        data-testid={`dsp-link-${r.id}-${k}`}
                        style={{ borderColor: `${PLATFORMS[k]?.color}66`, color: PLATFORMS[k]?.color }}
                        className="text-[10px] px-1.5 py-0.5 rounded-full border bg-zinc-950 hover:brightness-125">
                        {PLATFORMS[k]?.label || k}
                      </a>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SongDetailDialog record={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function pickTrend(trend, window) {
  if (window === "all") return trend;
  const n = window === "6m" ? 6 : 12;
  return trend.slice(-n);
}

function Stat({ k, v, tone, testid }) {
  const c = tone === "emerald" ? "text-emerald-400" : tone === "rose" ? "text-rose-400" : "text-zinc-100";
  return (
    <div className="p-4 rounded-xl border border-zinc-800 bg-zinc-950/60">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div>
      <div className={`mt-1.5 text-2xl font-semibold ${c}`} data-testid={testid}>{v}</div>
    </div>
  );
}
function Card({ title, subtitle, action, children }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-4">
      <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
        <div>
          <div className="font-semibold text-sm">{title}</div>
          {subtitle && <div className="text-xs text-zinc-500">{subtitle}</div>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}
function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="bg-zinc-900 border-zinc-800 h-9 min-w-[140px] text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
function SongDetailDialog({ record, onClose }) {
  return (
    <Dialog open={!!record} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl" data-testid="dsp-detail-dialog">
        <DialogHeader>
          <DialogTitle>{record?.song_name || "—"}</DialogTitle>
          <DialogDescription className="text-zinc-500">
            {record?.artist_singer || "—"} · {record?.album || "—"}
          </DialogDescription>
        </DialogHeader>
        {record && (
          <div className="space-y-4 max-h-[65vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <Meta k="Language" v={record.language} />
              <Meta k="Label" v={record.label} />
              <Meta k="Composer" v={record.composer} />
              <Meta k="Lyricist" v={record.lyricist} />
              <Meta k="Release date" v={record.release_date} />
              <Meta k="Track no." v={record.track_no} />
              <Meta k="ISRC" v={record.isrc} mono />
              <Meta k="UPC" v={record.upc} mono />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Available On</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(record.links || {}).map(([k, url]) => url && (
                  <a key={k} href={url} target="_blank" rel="noreferrer"
                    data-testid={`dsp-detail-link-${k}`}
                    style={{ borderColor: PLATFORMS[k]?.color, color: PLATFORMS[k]?.color }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border bg-zinc-900 hover:bg-zinc-800 text-xs">
                    {PLATFORMS[k]?.label || k} <ExternalLink className="w-3 h-3" />
                  </a>
                ))}
                {Object.values(record.links || {}).filter(Boolean).length === 0 && (
                  <div className="text-xs text-zinc-500">No platform links imported yet.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
function Meta({ k, v, mono }) {
  return (
    <div className="p-2.5 rounded-lg bg-zinc-900/60 border border-zinc-800">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div>
      <div className={`mt-0.5 ${mono ? "font-mono text-xs" : "text-sm"} text-zinc-100`}>{v || "—"}</div>
    </div>
  );
}
